import React, { useState } from 'react';
import {
  Radio,
  Tag,
  Button,
  Modal,
  Space,
  Select,
} from 'antd';
import { toast } from '../../../lib/toast';
import { DownloadOutlined, UndoOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { invalidateListResource } from '../../../lib/query/invalidateResource';
import type { ColumnsType } from 'antd/es/table';
import {
  exportContactsByBulkOperation,
  getBulkOperations,
  revertContactImport,
  revertBulkFollowUp,
  type BulkOperationListItem,
} from '../api/bulkOperations';
import DataTable from '../../../components/DataTable';
import ListPage from '../../../components/list/ListPage';

const STATUS_COLORS: Record<string, string> = {
  Completed: 'green',
  Reverted: 'orange',
  Failed: 'red',
  Pending: 'blue',
};

const OPERATION_TYPES = [
  { value: '', label: 'All types' },
  { value: 'ContactImport', label: 'Contact Import' },
  { value: 'BulkFollowUp', label: 'Bulk Follow-up' },
];

const BulkOperationsRegistryPage: React.FC = () => {
  const queryClient = useQueryClient();
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [operationType, setOperationType] = useState<string | undefined>(undefined);
  const [revertingId, setRevertingId] = useState<string | null>(null);
  const [revertingType, setRevertingType] = useState<string | null>(null);
  const [revertMode, setRevertMode] = useState<'all' | 'openOnly'>('all');

  const { data, isLoading } = useQuery({
    queryKey: ['bulk-operations', pageNumber, pageSize, operationType],
    queryFn: () => getBulkOperations(pageNumber, pageSize, operationType),
  });

  const revertMutation = useMutation({
    mutationFn: ({ id, type }: { id: string; type: string }) => {
      if (type === 'BulkFollowUp') {
        return revertBulkFollowUp(id, { mode: revertMode });
      }
      return revertContactImport(id);
    },
    onSuccess: (result) => {
      toast.success(`Successfully reverted ${result.revertedCount} items.`);
      invalidateListResource(queryClient, 'contacts');
      queryClient.invalidateQueries({ queryKey: ['bulk-operations'] });
      queryClient.invalidateQueries({ queryKey: ['taskItems'] });
      queryClient.invalidateQueries({ queryKey: ['task-dashboard'] });
      setRevertingId(null);
      setRevertingType(null);
    },
    onError: () => {
      toast.error('Failed to revert operation.');
      setRevertingId(null);
      setRevertingType(null);
    },
  });

  const handleDownload = async (id: string, purpose: string | null) => {
    try {
      const blob = await exportContactsByBulkOperation(id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const label = (purpose ?? 'import').replace(/\s+/g, '-').toLowerCase();
      a.download = `import-${label}-${id.slice(0, 8)}.xlsx`;
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download import result.');
    }
  };

  const handleRevert = (id: string, operationType: string) => {
    setRevertingId(id);
    setRevertingType(operationType);
    setRevertMode('all');
  };

  const confirmRevert = () => {
    if (revertingId && revertingType) {
      revertMutation.mutate({ id: revertingId, type: revertingType });
    }
  };

  const columns: ColumnsType<BulkOperationListItem> = [
    {
      title: 'Type',
      dataIndex: 'operationType',
      key: 'operationType',
      width: 140,
      render: (type: string) => {
        const label = OPERATION_TYPES.find((o) => o.value === type)?.label ?? type;
        return <Tag>{label}</Tag>;
      },
    },
    {
      title: 'Purpose',
      dataIndex: 'purpose',
      key: 'purpose',
      ellipsis: true,
    },
    {
      title: 'Initiated By',
      dataIndex: 'initiatedByEmail',
      key: 'initiatedByEmail',
      width: 180,
    },
    {
      title: 'Date',
      dataIndex: 'initiatedAtUtc',
      key: 'initiatedAtUtc',
      width: 170,
      render: (date: string) => new Date(date).toLocaleString(),
    },
    {
      title: 'Items',
      key: 'items',
      width: 80,
      render: (_, record) => `${record.successCount}/${record.totalItems}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) =>
        record.status === 'Completed' ? (
          <Space size="small">
            {record.operationType === 'ContactImport' && (
              <Button
                type="link"
                size="small"
                icon={<DownloadOutlined />}
                onClick={() => handleDownload(record.id, record.purpose)}
              >
                Download
              </Button>
            )}
            <Button
              type="link"
              danger
              size="small"
              icon={<UndoOutlined />}
              onClick={() => handleRevert(record.id, record.operationType)}
            >
              Revert
            </Button>
          </Space>
        ) : null,
    },
  ];

  return (
    <ListPage
      title="Bulk Operations"
      subtitle="Registry of all bulk actions across the system"
    >
      <Space style={{ marginBottom: 16 }}>
        <Select
          value={operationType ?? ''}
          onChange={(val) => {
            setOperationType(val || undefined);
            setPageNumber(1);
          }}
          options={OPERATION_TYPES}
          style={{ width: 200 }}
        />
      </Space>

      <DataTable<BulkOperationListItem>
        columns={columns}
        dataSource={data?.items ?? []}
        loading={isLoading}
        rowKey="id"
        bordered
        striped
        pagination={{
          current: pageNumber,
          pageSize,
          total: data?.totalCount ?? 0,
          onChange: (page) => setPageNumber(page),
        }}
        scroll={{ x: 990 }}
      />

      <Modal
        title="Confirm Revert"
        open={revertingId !== null}
        onOk={confirmRevert}
        onCancel={() => {
          setRevertingId(null);
          setRevertingType(null);
        }}
        confirmLoading={revertMutation.isPending}
        okText="Yes, Revert"
        okButtonProps={{ danger: true }}
      >
        {revertingType === 'BulkFollowUp' ? (
          <>
            <p>
              This will revert the follow-ups created by this bulk operation.
              Select which follow-ups to revert:
            </p>
            <Radio.Group
              value={revertMode}
              onChange={(e) => setRevertMode(e.target.value)}
              style={{ marginTop: 8 }}
            >
              <Radio value="all">All follow-ups (including completed)</Radio>
              <Radio value="openOnly">Only open/in-progress follow-ups</Radio>
            </Radio.Group>
          </>
        ) : (
          <p>
            This will undo the selected bulk operation and remove all associated items.
            This action cannot be undone.
          </p>
        )}
      </Modal>
    </ListPage>
  );
};

export default BulkOperationsRegistryPage;
